import { FolderOpen, LogIn } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { PROVIDER_LABEL } from "@/desktop/chat";
import { runCommand } from "@/desktop/commands";
import { useDesktopUi, type AiProvider } from "@/desktop/ui";
import { useMe } from "@/lib/queries";
import { CopilotChat } from "@/plugins/copilot-chat";
import { ClaudeCodeSession, useClaudeCodeAuth } from "@/plugins/claude-code";
import { Spinner } from "@/components/ui/primitives";
import { AiPanel } from "./ai-panel";
import { ChatHeader, ProviderMark } from "./chat-header";
import { useWs } from "./context";

function Gate({
  provider,
  title,
  text,
  action,
}: {
  provider: AiProvider;
  title: string;
  text: string;
  action: ReactNode;
}) {
  return (
    <div className="flex h-full flex-col">
      <ChatHeader provider={provider} />
      <div className="flex flex-col items-center px-6 pt-12 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-border bg-bg-subtle shadow-[inset_0_1px_0_0_var(--highlight)]">
          <ProviderMark provider={provider} className="h-6 w-6" />
        </span>
        <p className="mt-4 text-[14px] font-semibold tracking-tight text-fg">{title}</p>
        <p className="mt-1.5 max-w-[260px] text-[12.5px] leading-relaxed text-fg-muted">{text}</p>
        <div className="mt-5">{action}</div>
      </div>
    </div>
  );
}

function LoginGate({ provider }: { provider: AiProvider }) {
  return (
    <Gate
      provider={provider}
      title={`Converse com o ${PROVIDER_LABEL[provider]}`}
      text="A IA e os plugins ficam na sua conta (grátis): entre para usar."
      action={
        <Button
          variant="primary"
          onClick={() =>
            useDesktopUi.getState().set({ login: "Entre para conversar com a IA sobre o seu código." })
          }
        >
          <LogIn className="h-3.5 w-3.5" /> Entrar
        </Button>
      }
    />
  );
}

/** Lateral direita dentro de um projeto: o chat do plugin escolhido (Claude ou GitHub Copilot). */
export function ChatSidebar({
  provider,
  visit,
}: {
  provider: AiProvider;
  visit: { previousVisitAt: string | null; changedFiles: number } | null;
}) {
  const { me } = useWs();
  if (me.user.isGuest) return <LoginGate provider={provider} />;
  return provider === "copilot" ? <CopilotChat /> : <ClaudeSide visit={visit} />;
}

/**
 * Claude: com a conta do Claude Code conectada, o painel já abre o Claude Code oficial com a caixa
 * de prompt; sem ela, o chat do Kompil (chave da API) ou a tela de entrar.
 */
function ClaudeSide({ visit }: { visit: { previousVisitAt: string | null; changedFiles: number } | null }) {
  const { auth, available } = useClaudeCodeAuth();
  if (available && !auth)
    return (
      <div className="flex justify-center pt-10">
        <Spinner />
      </div>
    );
  if (auth?.loggedIn) return <ClaudeCodeSession />;
  return <AiPanel visit={visit} />;
}

/** Lateral direita fora de um projeto: os chats trabalham com o contexto do projeto aberto. */
export function ChatSidebarOutside({ provider }: { provider: AiProvider }) {
  const { data: me } = useMe();
  if (me?.user.isGuest) return <LoginGate provider={provider} />;
  return (
    <Gate
      provider={provider}
      title="Abra um projeto"
      text={`O ${PROVIDER_LABEL[provider]} responde com o contexto do projeto aberto: arquivos, seleção e o que mudou.`}
      action={
        <Button variant="secondary" onClick={() => runCommand("open-project")}>
          <FolderOpen className="h-3.5 w-3.5" /> Abrir projeto…
        </Button>
      }
    />
  );
}
